import React, { useState } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { GET_COMMENTS, CREATE_COMMENT } from '../graphql/voting';
import { formatDate } from '../utils/dateUtils';

const CommentSection = ({ articleId }) => {
  const [commentText, setCommentText] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const client = useApolloClient();



  const { loading, error, data } = useQuery(GET_COMMENTS, {
    variables: {
      articleId,
      limit: 10,
      skip: 0
    }
  });

  const [createComment] = useMutation(CREATE_COMMENT, {
    onCompleted: (data) => {
      if (data.createComment.success) {
        setCommentText('');
        setIsAnonymous(false);
        setIsSubmitting(false);
        setErrorMessage('');
        // 重新獲取評論
        client.cache.evict({ fieldName: 'articleComments' });
        client.cache.evict({ fieldName: 'votingArticles' });
        client.cache.gc();
      } else {
        // 處理後端返回的錯誤
        const errorMsg = data.createComment.message || 
          (data.createComment.errors && data.createComment.errors.length > 0 
            ? data.createComment.errors[0].message 
            : '發表評論失敗');
        setErrorMessage(errorMsg);
        setIsSubmitting(false);
      }
    },
    onError: (error) => {
      console.error('發表評論失敗:', error);
      const errorMsg = error.message || 
        (error.graphQLErrors && error.graphQLErrors.length > 0 
          ? error.graphQLErrors[0].message 
          : '發表評論失敗，請稍後再試');
      setErrorMessage(errorMsg);
      setIsSubmitting(false);
    }
  });

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await createComment({
        variables: {
          input: {
            articleId,
            content: commentText.trim(),
            isAnonymous
          }
        }
      });
    } catch (error) {
      console.error('評論提交錯誤:', error);
      setIsSubmitting(false);
    }
  };

  const comments = data?.articleComments?.comments || [];

  return (
    <div>
      {/* 評論標題 */}
      <div className="p-4 border-b border-gray-100">
        <h3 className="text-base font-semibold text-gray-900">
          評論 ({data?.articleComments?.totalCount || 0})
        </h3>
      </div>

      {/* 發表評論 */}
      <div className="p-4 border-b border-gray-100">
        <form onSubmit={handleSubmitComment}>
          {/* 錯誤訊息 */}
          {errorMessage && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errorMessage}</p>
            </div>
          )}
          <div className="mb-3">
            <textarea
              value={commentText}
              onChange={(e) => {
                setCommentText(e.target.value);
                // 清除錯誤訊息當用戶開始輸入時
                if (errorMessage) setErrorMessage('');
              }}
              placeholder="分享你的想法..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
              rows="3"
              maxLength="500"
            />
          </div>
          {/* 匿名發布選項 */}
          <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 cursor-pointer">
                🎭 匿名發布
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setIsAnonymous(true)}
                  className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                    isAnonymous
                      ? 'bg-purple-500 text-white border-purple-500'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                  }`}
                >
                  是
                </button>
                <button
                  type="button"
                  onClick={() => setIsAnonymous(false)}
                  className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                    !isAnonymous
                      ? 'bg-purple-500 text-white border-purple-500'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                  }`}
                >
                  否
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-600">
              啟用後，你的身份將對所有人隱藏
            </p>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {commentText.length}/500
            </span>
            <button
              type="submit"
              disabled={!commentText.trim() || isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              {isSubmitting ? '發表中...' : '發表評論'}
            </button>
          </div>
        </form>
      </div>

      {/* 評論列表 */}
      <div className="p-4">
        {loading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : error ? (
          <div className="text-center py-4 text-sm text-red-600">
            載入評論失敗
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-500">
            還沒有評論，來發表第一個評論吧！
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => {
              const isCommentAnonymous = comment.isAnonymous || !comment.author;
              const displayUsername = isCommentAnonymous ? '匿名使用者' : (comment.author?.username || '匿名使用者');
              const displayInitial = isCommentAnonymous ? '匿' : (comment.author?.username?.charAt(0)?.toUpperCase() || 'U');
              
              return (
                <div key={comment.id} className="border-b border-gray-100 pb-4 last:border-b-0">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isCommentAnonymous ? 'bg-gray-200' : 'bg-blue-100'
                      }`}>
                        <span className={`font-medium text-sm ${
                          isCommentAnonymous ? 'text-gray-600' : 'text-blue-600'
                        }`}>
                          {displayInitial}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-sm text-gray-900">
                          {isCommentAnonymous && '🎭 '}
                          {displayUsername}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(comment.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {comment.content}
                      </p>
                    {/* 暫時隱藏：讚和回覆功能目前不支援 */}
                    {/* <div className="flex items-center space-x-4 mt-2">
                      <button className="text-xs text-gray-500 hover:text-blue-600">
                        👍 讚 ({comment.likeCount || 0})
                      </button>
                      <button className="text-xs text-gray-500 hover:text-blue-600">
                        💬 回覆 ({comment.replyCount || 0})
                      </button>
                    </div> */}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentSection; 